import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Panel } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { EMPTY_DRAFT, RuleForm, type RuleDraft } from '../features/automations/RuleForm';
import { trpc } from '../lib/trpc';

/**
 * `?template=` — which suggestion to start from, if any.
 *
 * SHAPE-CHECKED ONLY, like the report filters. `validateSearch` runs during routing, so anything
 * it imports is in the eager bundle; the template vocabulary lives in `@promanage/core/automation`,
 * which is deliberately not. An id nobody recognises simply yields a blank form, which is the right
 * outcome for a stale bookmark and needs no error page.
 */
export const Route = createFileRoute('/automations/new')({
  validateSearch: (search: Record<string, unknown>): { template?: string } =>
    typeof search['template'] === 'string' ? { template: search['template'] } : {},
  component: NewAutomationPage,
});

function NewAutomationPage() {
  const { template } = Route.useSearch();
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const catalogue = useQuery(
    trpc.automation.catalogue.queryOptions(undefined, { staleTime: 60 * 60_000 }),
  );

  const [draft, setDraft] = useState<RuleDraft | null>(null);

  /*
   * The suggestion, once the catalogue has arrived — and only until the operator touches anything.
   *
   * DERIVED RATHER THAN COPIED IN AN EFFECT. The template is only the INITIAL value: an effect that
   * wrote it into state would race the first keystroke on a slow connection and silently discard
   * what somebody had typed.
   */
  const seeded =
    draft ??
    (() => {
      const found = catalogue.data?.templates.find((entry) => entry.id === template);
      if (!found) return EMPTY_DRAFT;
      return {
        name: found.name,
        triggerEvent: found.trigger,
        // Templates carry leaf conditions only, which is what the form authors.
        conditions: found.conditions as RuleDraft['conditions'],
        action: found.action,
        config: found.config as RuleDraft['config'],
      };
    })();

  const create = useMutation(
    trpc.automation.create.mutationOptions({
      meta: { errorTitle: 'Could not create that automation' },
      onSuccess(created) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.automation.overview.queryKey() });
        void navigate({ to: '/automations/$id', params: { id: created.id } });
      },
    }),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New automation"
        subtitle="When something happens, do one thing"
        actions={
          <Link to="/automations">
            <Button size="sm" variant="secondary">
              All automations
            </Button>
          </Link>
        }
      />

      {catalogue.isPending ? (
        <Panel className="p-6">
          <p className="text-[0.8125rem] text-ink-muted">Loading…</p>
        </Panel>
      ) : (
        <RuleForm
          draft={seeded}
          onChange={(next) => {
            unsaved.touch();
            setDraft(next);
          }}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => {
            unsaved.reset();
            void navigate({ to: '/automations' });
          }}
          submitLabel="Create automation"
          pending={create.isPending}
        />
      )}
    </div>
  );
}
